import React, { useEffect, useState } from 'react';
import { FeeConfiguration, ChangedItem } from '@/types';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import FeeEditorItem from './FeeEditorItem';
import { Button } from '@/components/Buttons';
import { faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { getUniqueId } from '@/utils/html';
import { EditorFeeConfiguration } from '../FeeEditor';
import FeePreview from '../FeePreview';
import theme from './FeeEditor.module.scss';
import { filterNumericInput, getNumberValue } from '@/utils/string';
import { InputWrapper, Tooltip, ValidatingInput } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from '@lingui/core/macro';
import { AlertBanner } from '@ygtechnologies/rubus-ui';

interface Props {
    value: EditorFeeConfiguration[];
    onChange(newValue: FeeConfiguration[]): void;
    previewBasePrice?: number;
    previewQuantity?: number;
}

const FeeEditor: React.FC<Props> = ({ value, onChange, previewBasePrice = 1000, previewQuantity = 1 }) => {
    const currentVatPercentage = value.length > 0 ? value[0].taxPercentage || 0 : 0;
    const [vatInputValue, setVatInputValue] = useState(currentVatPercentage.toString());

    useEffect(() => {
        // Only re-sync when the prop differs numerically; otherwise an in-progress
        // "7." (== 7) would get rewritten to "7", eating the decimal point.
        if (getNumberValue(vatInputValue) !== currentVatPercentage) {
            setVatInputValue(currentVatPercentage.toString());
        }
    }, [currentVatPercentage]);

    useEffect(() => {
        if (value.length > 0) {
            const lastLayer = value[value.length - 1];
            const referenceTaxPercentage = lastLayer?.taxPercentage || 0;

            const needsSync = value.some((layer) => layer.taxPercentage !== referenceTaxPercentage);

            if (needsSync) {
                const syncedValue = value.map((layer) => ({
                    ...layer,
                    taxPercentage: referenceTaxPercentage,
                }));
                onChange(syncedValue as FeeConfiguration[]);
            }
        }
    }, [value.length]);

    const handleChange = (changes: ChangedItem<EditorFeeConfiguration>) => {
        var newValue = arrayReplace(value, changes.original, changes.modified);
        onChange(newValue as FeeConfiguration[]);
    };

    const handleDeleteClick = (item: EditorFeeConfiguration) => {
        onChange(value.filter((x) => x !== item) as any);
    };

    const handleVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filteredValue = filterNumericInput(e.target.value);
        setVatInputValue(filteredValue); // Store the filtered input value

        const taxPercentage = getNumberValue(filteredValue);
        const newValue = value.map((layer) => ({
            ...layer,
            taxPercentage: taxPercentage,
        }));
        onChange(newValue as FeeConfiguration[]);
    };

    const handleAddRow = () => {
        const newValue: EditorFeeConfiguration[] = [
            ...value,
            {
                _internalId: getUniqueId(),
                id: 0,
                name: '',
                feePercentage: 0,
                taxPercentage: currentVatPercentage,
                sortOrder: 0,
            },
        ];

        onChange(newValue as FeeConfiguration[]);
    };

    return (
        <div className={theme.container}>
            {value.length > 0 ? (
                <div className={theme.main}>
                    <div className="form columns-1 labels-20">
                        {value.map((row) => (
                            <div className={theme.item} key={row._internalId || row.id}>
                                <FeeEditorItem value={row} onChange={handleChange} onDeleteClick={handleDeleteClick} />
                            </div>
                        ))}
                        <hr />

                        <div className={theme.vatSection}>
                            <div className="form-group">
                                <label>
                                    <div className={theme.vatLabel}>
                                        <Trans>VAT</Trans>
                                        <Tooltip content={<Trans>This percentage is VAT applied to all layers</Trans>}>
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                        </Tooltip>
                                    </div>

                                    <InputWrapper appendContent="%">
                                        <ValidatingInput
                                            className="form-control no-spinner"
                                            type="text"
                                            value={vatInputValue}
                                            placeholder={t`VAT percentage`}
                                            onChange={handleVatChange}
                                        />
                                    </InputWrapper>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className={theme.preview}>
                        <h3>
                            <Trans>Preview</Trans>
                        </h3>
                        <FeePreview
                            configuration={value as FeeConfiguration[]}
                            basePrice={previewBasePrice}
                            quantity={previewQuantity}
                        />
                    </div>
                </div>
            ) : (
                <AlertBanner type="info">
                    <Trans>
                        Currently no company charges any fees on top of the prices that the subcontractors take. Click on
                        "Add a layer" in order to add a layer of fees. There can be several layers in a given project.
                    </Trans>
                </AlertBanner>
            )}

            <Button color="link" icon={faPlus} onClick={handleAddRow}>
                <Trans>Add a layer</Trans>
            </Button>
        </div>
    );
};

export default FeeEditor;
